import React, { useEffect, useState } from "react";
import { TrendingUp, Info } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { authApi } from "../../lib/auth";

const fmtShort = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
const fmtLong = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

function TrendTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="cis-tip glass" data-testid="cis-trend-tooltip">
      <b>{fmtLong(p.at)}</b>
      <span>
        Level 1: <b className="mono">{p.l1 ?? "–"}%</b> · {p.pass} pass / {p.fail} fail
      </span>
      <span>
        Level 1 + 2: <b className="mono">{p.l2 ?? "–"}%</b> · {p.l2_pass} pass / {p.l2_fail} fail
      </span>
      <span className="muted small">
        {p.unassessed} not assessed · {p.exception} accepted risk{p.auto ? " · scheduled" : ""}
      </span>
    </div>
  );
}

/** L1 / L1+2 compliance score across all finished CIS scans of a tenant. */
export default function ScoreTrend({ tenantId, e5, refreshKey }) {
  const [points, setPoints] = useState(null);
  useEffect(() => {
    authApi.compliance
      .trend(tenantId, { e5 })
      .then((d) => setPoints(d.points))
      .catch(() => setPoints([]));
  }, [tenantId, e5, refreshKey]);
  const data = (points || []).map((p, i) => ({ ...p, idx: i, label: fmtShort(p.at) }));
  const first = data[0];
  const last = data[data.length - 1];
  const delta = first && last && first.l1 !== null && last.l1 !== null ? last.l1 - first.l1 : null;
  return (
    <section className="ws-card glass" data-testid="cis-trend">
      <div className="ws-card-head">
        <h2>
          <TrendingUp size={15} /> Score trend
        </h2>
        {data.length > 1 && delta !== null && (
          <span className={`mini-chip ${delta > 0 ? "ok" : delta < 0 ? "crit" : ""}`} data-testid="cis-trend-delta">
            {delta > 0 ? "+" : ""}
            {delta} pts over {data.length} scans
          </span>
        )}
      </div>
      {points === null && <p className="muted">Loading...</p>}
      {points && data.length < 2 && (
        <div className="ws-alert" data-testid="cis-trend-empty">
          <Info size={13} /> Run another CIS scan (or turn on scheduled scans) to see how the score moves over time.
        </div>
      )}
      {data.length > 1 && (
        <div className="cis-trend-chart" data-testid="cis-trend-chart">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data} margin={{ top: 8, right: 16, left: -12, bottom: 0 }}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "var(--text-3)", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fill: "var(--text-3)", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
              <Tooltip content={<TrendTip />} cursor={{ stroke: "var(--border)" }} />
              <Legend wrapperStyle={{ fontSize: 12, color: "var(--text-2)" }} />
              <Line type="monotone" dataKey="l1" name="Level 1" stroke="var(--accent)" strokeWidth={2.5} dot={{ r: 3.5 }} activeDot={{ r: 5 }} connectNulls />
              <Line type="monotone" dataKey="l2" name="Level 1 + 2" stroke="var(--violet)" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}
